import * as React from 'react';
import styled from 'styled-components';
import { __ } from 'react-i18n';
import { Select } from '../../General/Select';
import {
  FlexCol,
  FlexRow,
  FlexRowCenterVertical,
} from '../../../Defaults/Flexbox/Flexbox';
import { FormWrapper, FormInput } from '../../Form/FormInput';
import { CheckBox } from '../../General/Checkbox';
import { CompanyInfo } from './CompanyInfo';
import { prop } from '../../../../utilities';

interface Props {
  isCompany: boolean;
  billingAddress: any;
  company: any;
  onBlur: () => void;
  billing_company: boolean;
  use_delivery_address: boolean;
  formProblems: any;
  isB2b: boolean;
  handleChange: (data: { field: string; value: string | number }) => {};
  eshopUser: boolean;
}

function BillingAddress({
  billingAddress,
  company,
  onBlur,
  billing_company,
  use_delivery_address,
  formProblems,
  isB2b,
  handleChange,
  eshopUser,
}: Props) {
  const initialBillingAddressState = getInitialBillingState(billingAddress);
  const {
    name,
    surname,
    street,
    street_number,
    city,
    zip,
    country,
  } = initialBillingAddressState;
  const initialCompanyState = getInitialCompanyState(company);

  const {
    nameProblem,
    surnameProblem,
    streetProblem,
    streetNrProblem,
    cityProblem,
    zipProblem,
  } = {
    nameProblem: prop(formProblems, 'billing_address.person.name'),
    surnameProblem: prop(formProblems, 'billing_address.person.surname'),
    streetProblem: prop(formProblems, 'billing_address.street'),
    streetNrProblem: prop(formProblems, 'billing_address.street_number'),
    cityProblem: prop(formProblems, 'billing_address.city'),
    zipProblem: prop(formProblems, 'billing_address.zip'),
  };

  const onChange = event => {
    handleChange({ field: event.target.name, value: event.target.value });
  };

  const readOnly = eshopUser ? isB2b : false;

  return (
    <Wrapper>
      <NameWrapper>
        <FormInput
          name={'billing_address.person.name'}
          type="text"
          label={'Meno'}
          required={!isB2b}
          defaultValue={name}
          marginBottom={16}
          minWidthPx={288}
          marginRight={32}
          onChange={onChange}
          onBlur={onBlur}
          validMessage={nameProblem}
          readOnly={readOnly}
        />
        <FormInput
          name={'billing_address.person.surname'}
          type="text"
          required={!isB2b}
          defaultValue={surname}
          label={'Priezvisko'}
          minWidthPx={288}
          marginBottom={16}
          onChange={onChange}
          onBlur={onBlur}
          validMessage={surnameProblem}
          readOnly={readOnly}
        />
      </NameWrapper>
      <AddressWrapper>
        <FormWrapper noWrap={true} marginRight={32}>
          <FormInput
            name="billing_address.street"
            type="text"
            label={__('Ulica')}
            defaultValue={street}
            maxWidth={100}
            maxWidthPx={224}
            required={true}
            marginRight={16}
            onChange={onChange}
            onBlur={onBlur}
            validMessage={streetProblem}
          />
          <FormInput
            name="billing_address.street_number"
            type="text"
            label={__('Číslo')}
            defaultValue={street_number}
            maxWidth={34}
            required={true}
            maxWidthPx={80}
            onChange={onChange}
            onBlur={onBlur}
            validMessage={streetNrProblem}
          />
        </FormWrapper>
        <FormWrapper noWrap={true}>
          <FormInput
            name="billing_address.city"
            type="text"
            label={__('Mesto')}
            defaultValue={city}
            maxWidth={100}
            maxWidthPx={224}
            marginRight={16}
            required={true}
            onChange={onChange}
            onBlur={onBlur}
            validMessage={cityProblem}
          />
          <FormInput
            name="billing_address.zip"
            type="text"
            label={__('PSČ')}
            defaultValue={zip}
            maxWidth={34}
            maxWidthPx={80}
            required={true}
            onChange={onChange}
            onBlur={onBlur}
            validMessage={zipProblem}
          />
        </FormWrapper>{' '}
      </AddressWrapper>
      <Select
        marginBottom={32}
        name="billing_address.country"
        label={__('Krajina')}
        value={country}
        required={true}
        options={[{ value: 'sk', name: 'Slovensko' }]}
        onChange={onChange}
        onBlur={onBlur}
      />
      <CheckBox
        name="billing_company"
        onClick={onChange}
        selected={billing_company}
        label={__('Nakupujem na firmu')}
      />
      <CompanyInfo
        company={initialCompanyState}
        onChange={onChange}
        onBlur={onBlur}
        isVisible={billing_company}
        problems={prop(formProblems, 'company')}
        isB2b={isB2b}
      />
      <CheckBox
        name="use_delivery_address"
        onClick={onChange}
        selected={use_delivery_address}
        label={__('Dodacia adresa je iná ako fakturačná adresa')}
      />
    </Wrapper>
  );
}

const getInitialBillingState = (billingAddress: any) => {
  return {
    name: prop(billingAddress, 'person.name', ''),
    surname: prop(billingAddress, 'person.surname', ''),
    street: prop(billingAddress, 'street', ''),
    street_number: prop(billingAddress, 'street_number', ''),
    city: prop(billingAddress, 'city', ''),
    zip: prop(billingAddress, 'zip', ''),
    country: prop(billingAddress, 'country', 'sk'),
  };
};

const getInitialCompanyState = (company: any) => {
  return {
    companyName: prop(company, 'name', ''),
    dic: prop(company, 'dic', ''),
    ic_dph: prop(company, 'ic_dph', ''),
    ico: prop(company, 'ico', ''),
  };
};

const Wrapper = styled(FlexCol)``;

const NameWrapper = styled(FlexRow)`
  flex-flow: row wrap;
`;

const AddressWrapper = styled(FlexRowCenterVertical)`
  flex-flow: row wrap;
`;

export { BillingAddress };
